import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Scale } from 'lucide-react';
import api from '../lib/api';
import { setPageMeta, mdToHtml } from '../lib/shop';

export default function Docs() {
  const { slug } = useParams();
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(null);

  useEffect(() => {
    api.get('/docs').then((r) => setPages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) { setPage(null); setPageMeta('Documentation Centre', 'Guides, policies and research documentation for VQ Health.'); return; }
    api.get(`/docs/${slug}`).then((r) => { setPage(r.data); setPageMeta(r.data.title); }).catch(() => setPage(false));
  }, [slug]);

  if (slug) {
    if (page === null) return <div className="max-w-3xl mx-auto px-4 py-20"><div className="h-40 bg-[#F4F4F0] animate-pulse" /></div>;
    if (page === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center font-display font-bold uppercase text-2xl">Page not found</div>;
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10" data-testid="doc-page">
        <DocsNav pages={pages} active={slug} />
        <div className="max-w-3xl">
          {page.legal_review && (
            <div className="mb-6 bg-[#FFF7ED] border border-[#FDE68A] p-3.5 flex items-center gap-3" data-testid="legal-review-flag">
              <Scale className="w-4 h-4 text-[#D97706]" />
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#92400E]">Draft — legal review required before final publication</p>
            </div>
          )}
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{page.title}</h1>
          <div className="vq-prose mt-8" dangerouslySetInnerHTML={{ __html: mdToHtml(page.content) }} />
        </div>
      </div>
    );
  }

  const groups = pages.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="docs-home">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Help & Reference</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-10">Documentation Centre</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {Object.entries(groups).map(([cat, list]) => (
          <div key={cat}>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A8F95] mb-4 pb-2 border-b hairline">{cat}</p>
            <div className="space-y-1">
              {list.map((p) => (
                <Link key={p.id} to={`/docs/${p.slug}`} data-testid={`doc-link-${p.slug}`}
                  className="flex items-center justify-between py-2.5 px-3 hover:bg-white border border-transparent hover:border-[#E5E5DF] transition-colors group">
                  <span className="font-display font-bold uppercase tracking-wide">{p.title}</span>
                  <ChevronRight className="w-4 h-4 text-[#D8D8D0] group-hover:text-[#0B0F12]" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocsNav({ pages, active }) {
  return (
    <aside className="hidden lg:block">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-3">Documentation</p>
      <div className="space-y-0.5">
        {pages.map((p) => (
          <Link key={p.id} to={`/docs/${p.slug}`}
            className={`block px-3 py-2 text-sm border-l-2 transition-colors ${p.slug === active ? 'border-[#0B0F12] font-semibold' : 'border-transparent text-[#575B60] hover:text-[#0B0F12]'}`}>
            {p.title}
          </Link>
        ))}
      </div>
    </aside>
  );
}
